import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
const _excluded = ["unitId"];
import { BackdropLoader, Icon, toast, InputWithIcon, SelectMenu } from '@livable/saas-react-components';
import { DEMO_PROPERTY_STEPS, APPFOLIO_PROPERTY_STEPS, PROPERTY_STEPS } from 'src/constants/property';
import { Typography, Button, Paper, TextField, Chip, MenuItem } from '@mui/material';
import { singleElementOfArray } from 'src/utils/singleElementOfArray';
import { ALLOCATION_METHOD } from 'src/constants/utilities';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { PmsUnitMappingModal } from './PmsUnitMappingModal';
import UnitCounter from './UnitCounter';
import { isConvergentExportEnabled } from 'src/utils/convergentExport';
import { useStore } from 'src/stores';
import { UnitForm } from './UnitForm';
import PropTypes from 'prop-types';
import useStyles from './styles';
export const PropertyUnitSetupPage = observer(_ref => {
  var _store$company$proper, _store$company$proper2, _store$addNewProperty6, _store$addNewProperty7;
  let _ref$handleStep = _ref.handleStep,
    handleStep = _ref$handleStep === void 0 ? () => {} : _ref$handleStep,
    editMode = _ref.editMode,
    mutablePropertyId = _ref.mutablePropertyId,
    notCompletedPropertyId = _ref.notCompletedPropertyId,
    registerActionBack = _ref.registerActionBack,
    registerActionNext = _ref.registerActionNext;
  const store = useStore();
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchByUnitName = _useState2[0],
    setSearchByUnitName = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoaded = _useState4[0],
    setIsLoaded = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    buildingId = _useState6[0],
    setBuildingId = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isSubmited = _useState8[0],
    setIsSubmited = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    unitsToUpdate = _useState0[0],
    setUnitsToUpdate = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    requiredSquareFootage = _useState10[0],
    setRequiredSquareFootage = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    utilityType = _useState12[0],
    setUtilityType = _useState12[1];
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isPmsMappingOpen = _useState14[0],
    setIsPmsMappingOpen = _useState14[1];

  // The mapping exists to serve the convergent charge export, so it lives behind the same gate
  // as the export button, plus a PMS to map to. The property is only in the dashboard once it
  // exists, so during the add flow the company setting stands in for its override.
  const pms = (_store$company$proper = (_store$company$proper2 = store.company.propertyManagementSoftware) === null || _store$company$proper2 === void 0 ? void 0 : _store$company$proper2.trim()) !== null && _store$company$proper !== void 0 ? _store$company$proper : '';
  const currentProperty = store.homeDashboardStore.getCurrentProperty;
  const propertyDashboard = (currentProperty === null || currentProperty === void 0 ? void 0 : currentProperty.id) === mutablePropertyId ? currentProperty === null || currentProperty === void 0 ? void 0 : currentProperty.dashboard : null;
  const showPmsUnitId = isConvergentExportEnabled(store, propertyDashboard) && pms.length > 0 && pms.toLowerCase() !== 'none';
  const CURRENT_STEPS = store.company.companyFeatureStore.isAppfolio ? APPFOLIO_PROPERTY_STEPS : PROPERTY_STEPS;
  const isSubmitDisabled = () => {
    let result = false;
    for (var i = 0; i < store.addNewProperty.unitSetup.unitsValidation.length; i++) {
      for (const _ref2 of Object.entries(store.addNewProperty.unitSetup.unitsValidation[i])) {
        var _ref3 = _slicedToArray(_ref2, 2);
        const value = _ref3[1];
        if (value === false) {
          result = true;
          break;
        }
      }
    }
    return result;
  };
  const handleSubmit = async () => {
    setIsSubmited(true);
    if (requiredSquareFootage) {
      for (let i = 0; i < store.addNewProperty.unitSetup.units.length; i++) {
        const element = store.addNewProperty.unitSetup.units[i];
        if (!(element.square > 0)) {
          store.addNewProperty.unitSetup.validateValue(i, 'square', false);
        }
      }
    }
    if (isSubmitDisabled()) return;
    const unitsToUpdateList = [...unitsToUpdate];
    const temporaryData = [];
    let temporaryBuildingId;
    if (buildingId === '') {
      var _singleElementOfArray;
      const buildingsData = await store.buildings.getBuildingAsync.run({
        propertyId: notCompletedPropertyId ? notCompletedPropertyId : localStorage.getItem('livable_property_id')
      });
      const currentBuildingId = (_singleElementOfArray = singleElementOfArray(buildingsData === null || buildingsData === void 0 ? void 0 : buildingsData.response)) === null || _singleElementOfArray === void 0 ? void 0 : _singleElementOfArray.id;
      setBuildingId(currentBuildingId);
      temporaryBuildingId = currentBuildingId;
    }
    const data = {
      buildingId: !!buildingId && buildingId !== '' ? buildingId : temporaryBuildingId,
      units: {
        toCreate: [],
        toUpdate: [],
        toDelete: []
      }
    };
    store.addNewProperty.unitSetup.units.map(item => {
      const itemAmenities = [];
      item.amenities.map(amenityItem => {
        itemAmenities.push({
          inUnitAmenityId: amenityItem.inUnitAmenityId,
          inUnitAmenityOptionId: amenityItem.inUnitAmenityOptionId
        });
      });
      temporaryData.push({
        unitId: item.id,
        name: item.name.trim(),
        occupancy: +item.occupancy,
        squareFootage: +item.square,
        type: item.type,
        inUnitAmenities: itemAmenities,
        optOutUtilityIds: item.disabledOptOutUtilityIds.slice(),
        unitUtilities: item.unitUtilities.slice()
      });
    });

    // Which row of the list each created unit came from, in the order the request sends them —
    // the response returns the new ids in that same order, and nothing else ties them to a row.
    const createdFromIndex = [];
    temporaryData.forEach((item, index) => {
      const coincidenceIndex = unitsToUpdateList.findIndex(feeItem => feeItem.id === item.unitId);
      if (coincidenceIndex !== -1) {
        data.units.toUpdate.push(item);
        unitsToUpdateList.splice(coincidenceIndex, 1);
      } else {
        const unitId = item.unitId,
          currentItem = _objectWithoutProperties(item, _excluded);
        data.units.toCreate.push(currentItem);
        createdFromIndex.push(index);
      }
    });
    unitsToUpdateList.map(item => {
      data.units.toDelete.push(item.id);
    });
    const resp = await store.units.updateUnitsAsync.run(data);
    if (resp && resp.isSuccessful) {
      store.addNewProperty.unitSetup.initIdsAfterAddingUnits(resp.response);

      // A second call, and deliberately so: the unit PATCH above rebuilds each unit from the
      // whole model, while this one touches the PMS columns alone.
      //
      // The ids come from what was sent plus what came back, not from the store: the line above
      // writes the created ids into units[0..n] by position, which is only the right row when
      // every unit is new.
      if (showPmsUnitId) {
        const savedBuildingId = !!buildingId && buildingId !== '' ? buildingId : temporaryBuildingId;
        const unitIds = temporaryData.map(item => item.unitId);
        createdFromIndex.forEach((unitIndex, createdIndex) => {
          var _resp$response;
          unitIds[unitIndex] = (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response[createdIndex];
        });
        const mappings = unitIds.map((unitId, index) => {
          var _store$addNewProperty, _store$addNewProperty2, _store$addNewProperty3, _store$addNewProperty4;
          return {
            unitId,
            pmsUnitId: (_store$addNewProperty = (_store$addNewProperty2 = store.addNewProperty.unitSetup.units[index]) === null || _store$addNewProperty2 === void 0 ? void 0 : _store$addNewProperty2.pmsUnitId) !== null && _store$addNewProperty !== void 0 ? _store$addNewProperty : '',
            pmsUnitName: (_store$addNewProperty3 = (_store$addNewProperty4 = store.addNewProperty.unitSetup.units[index]) === null || _store$addNewProperty4 === void 0 ? void 0 : _store$addNewProperty4.pmsUnitName) !== null && _store$addNewProperty3 !== void 0 ? _store$addNewProperty3 : ''
          };
        }).filter(mapping => mapping.unitId && mapping.unitId !== 'null');
        if (mappings.length > 0) {
          await store.units.updateUnitPmsMappingsAsync.run({
            buildingId: savedBuildingId,
            mappings
          });
        }
      }
      if (!mutablePropertyId) {
        const currentPropertyId = notCompletedPropertyId ? notCompletedPropertyId : localStorage.getItem('livable_property_id');
        await store.properties.updatePropertySetupProgressAsync.run({
          propertyId: currentPropertyId,
          propertySetupStep: localStorage.getItem('livable_demo_mode') !== 'Demo' ? Object.keys(CURRENT_STEPS).find(key => CURRENT_STEPS[key] === CURRENT_STEPS.UnitSetup) : Object.keys(DEMO_PROPERTY_STEPS).find(key => DEMO_PROPERTY_STEPS[key] === DEMO_PROPERTY_STEPS.UnitSetup)
        });
        if (store.company.companyFeatureStore.isAppfolio) {
          store.addNewProperty.changeCompletedStep(APPFOLIO_PROPERTY_STEPS.FeeSetup);
          handleStep(APPFOLIO_PROPERTY_STEPS.FeeSetup)();
        } else {
          if (localStorage.getItem('livable_demo_mode') !== 'Demo') {
            store.addNewProperty.changeCompletedStep(PROPERTY_STEPS.ConfirmProperty);
            handleStep(PROPERTY_STEPS.ConfirmProperty)();
          } else {
            store.addNewProperty.changeCompletedStep(DEMO_PROPERTY_STEPS.ConfirmProperty);
            handleStep(DEMO_PROPERTY_STEPS.ConfirmProperty)();
          }
        }
      } else {
        const updatedUnits = await store.units.getUnitsAsync.run({
          buildingId: buildingId,
          applyToHomeDashboard: false
        });
        setUnitsToUpdate(updatedUnits === null || updatedUnits === void 0 ? void 0 : updatedUnits.response);
      }
      if (mutablePropertyId) {
        toast('success', {
          body: ['Successfully saved']
        });
      }
    }
  };
  const startRerender = () => {
    setIsLoaded(true);
    setTimeout(() => {
      setIsLoaded(false);
    }, 100);
  };
  useEffect(() => {
    const getAllAmenities = async () => {
      const resp = await store.inUnitAmenities.getAllInUnitAmenitiesAsync.run();
      return resp;
    };
    getAllAmenities().then(async () => {
      var _singleElementOfArray2, _store$addNewProperty5;
      const currentPropertyId = mutablePropertyId ? mutablePropertyId : notCompletedPropertyId ? notCompletedPropertyId : localStorage.getItem('livable_property_id');
      const buildingsData = await store.buildings.getBuildingAsync.run({
        propertyId: currentPropertyId
      });
      const currentBuildingId = (_singleElementOfArray2 = singleElementOfArray(buildingsData === null || buildingsData === void 0 ? void 0 : buildingsData.response)) === null || _singleElementOfArray2 === void 0 ? void 0 : _singleElementOfArray2.id;
      setBuildingId(currentBuildingId);
      const units = await store.units.getUnitsAsync.run({
        buildingId: currentBuildingId,
        applyToHomeDashboard: false
      });
      setUnitsToUpdate(units === null || units === void 0 ? void 0 : units.response);
      const utilities = await store.properties.getPropertyUtilityProvidersV3Async.run({
        propertyId: currentPropertyId
      });
      for (let i = 0; i < utilities.response.length; i++) {
        const result = utilities === null || utilities === void 0 ? void 0 : utilities.response[i].utilities.some(item => item.allocationMethod === ALLOCATION_METHOD.BySquareFootage || item.allocationMethod === ALLOCATION_METHOD.By50Occupancy50SquareFootage);
        if (result) {
          setRequiredSquareFootage(true);
          break;
        }
      }
      const utilityTemplatesIdx = (_store$addNewProperty5 = store.addNewProperty.utilitiesSetupV2.selectedUtilities) === null || _store$addNewProperty5 === void 0 ? void 0 : _store$addNewProperty5.findIndex(i => i.allocationMethod === ALLOCATION_METHOD.ByPercentageShare);
      if (utilityTemplatesIdx !== -1) {
        setUtilityType(store.addNewProperty.utilitiesSetupV2.selectedUtilities[utilityTemplatesIdx].utilityId);
      }
      setIsLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (registerActionBack !== undefined) registerActionBack(() => {
      handleStep(store.company.companyFeatureStore.isAppfolio ? APPFOLIO_PROPERTY_STEPS.UtilityConfiguration : localStorage.getItem('livable_demo_mode') !== 'Demo' ? PROPERTY_STEPS.UtilityConfiguration : DEMO_PROPERTY_STEPS.UtilityConfiguration)();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerActionBack]);
  useEffect(() => {
    if (registerActionNext !== undefined) registerActionNext(() => {
      handleSubmit();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerActionNext]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paper, {
    variant: "evenWrapper"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('add_new_property_flow.unit_setup')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.xsSection
  }, t('add_new_property_flow.unit_setup_hint'))), /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement("div", {
    className: classes.address
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, store.addNewProperty.propertyInformation.propertyAddress)), /*#__PURE__*/React.createElement("div", {
    className: classes.paperContent
  }, store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement("div", {
    className: classes.unitSearch
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    type: "text",
    variant: "outlined",
    value: searchByUnitName,
    label: t('Search for unit'),
    onChange: e => setSearchByUnitName(e.target.value)
  }))), !store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(UnitCounter, {
    value: store.addNewProperty.unitSetup.units.length,
    changeCount: store.addNewProperty.unitSetup.chageCount,
    withoutControllers: true
  }), showPmsUnitId && /*#__PURE__*/React.createElement("div", {
    className: classes.pmsMapping
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    className: classes.pmsMappingCount
  }, store.addNewProperty.unitSetup.mappedUnitsCount, " of ", store.addNewProperty.unitSetup.units.length, ' ', "units mapped to ", pms), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => setIsPmsMappingOpen(true),
    disabled: store.company.companyFeatureStore.readOnlyPropertyUnitSetup,
    'custom-attribute': '147-property-unit-setup-page'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "upload"
  }), /*#__PURE__*/React.createElement("span", null, t('Map from CSV'))))), /*#__PURE__*/React.createElement("div", {
    className: classes.validationCaption
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "formHint",
    state: store.addNewProperty.unitSetup.unitsValidation.findIndex(item => !item.name) !== -1 ? 'error' : ''
  }, /*#__PURE__*/React.createElement(Icon, {
    fontSize: "small",
    type: "warning"
  }), "unit name should not be empty"), /*#__PURE__*/React.createElement(Typography, {
    variant: "formHint",
    state: store.addNewProperty.unitSetup.unitsValidation.findIndex(item => !item.square) !== -1 ? 'error' : ''
  }, /*#__PURE__*/React.createElement(Icon, {
    fontSize: "small",
    type: "warning"
  }), "sq.footage can not be empty if enabled allocation method by 'sq.footage' or '50%Occupancy/50%Square Footage'")), /*#__PURE__*/React.createElement("div", {
    className: classes.unitForms
  }, store.addNewProperty.unitSetup.units.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, ((_store$addNewProperty6 = store.addNewProperty.utilitiesSetupV2.selectedUtilities) === null || _store$addNewProperty6 === void 0 ? void 0 : _store$addNewProperty6.findIndex(i => i.allocationMethod === ALLOCATION_METHOD.ByPercentageShare)) !== -1 && /*#__PURE__*/React.createElement("div", {
    className: classes.unitFormsPercentageShare
  }, /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "info",
      state: "info"
    }),
    label: t('Some utilities use a percentage-based allocation method. Please enter the percentage values for the units below.'),
    variant: "info"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.contentRow
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "unit-modal-utility-type-selector",
    labelId: "unit-modal-utility-type-selector-label",
    label: t('Allocation method'),
    value: utilityType,
    onChange: e => setUtilityType(e.target.value || ''),
    displayEmpty: true
  }, (_store$addNewProperty7 = store.addNewProperty.utilitiesSetupV2.selectedUtilities) === null || _store$addNewProperty7 === void 0 || (_store$addNewProperty7 = _store$addNewProperty7.filter(i => i.allocationMethod === ALLOCATION_METHOD.ByPercentageShare)) === null || _store$addNewProperty7 === void 0 ? void 0 : _store$addNewProperty7.map(utilityItem => /*#__PURE__*/React.createElement(MenuItem, {
    key: utilityItem.utilityId,
    value: utilityItem.utilityId
  }, utilityItem.name, " (", utilityItem.utilityProviderName, ")"))))), store.addNewProperty.unitSetup.units.map((elem, i) => {
    if (elem.name.toLocaleLowerCase().includes(searchByUnitName.toLocaleLowerCase())) {
      return /*#__PURE__*/React.createElement(UnitForm, {
        key: i,
        unitTypeProp: elem.type,
        unitIndex: i,
        isLoaded: isLoaded,
        isSubmited: isSubmited,
        utilityType: utilityType,
        requiredSquareFootage: requiredSquareFootage,
        showPmsUnitId: showPmsUnitId,
        _startRerender: startRerender
      });
    }
  }))), showPmsUnitId && /*#__PURE__*/React.createElement(PmsUnitMappingModal, {
    isOpen: isPmsMappingOpen,
    onClose: () => setIsPmsMappingOpen(false),
    units: store.addNewProperty.unitSetup.units,
    onApply: mappings => store.addNewProperty.unitSetup.applyPmsMappings(mappings)
  }), /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: store.units.getUnitsAsync.inProgress || store.units.updateUnitPmsMappingsAsync.inProgress || store.units.updateUnitsAsync.inProgress || store.buildings.getBuildingAsync.inProgress || store.inUnitAmenities.getAllInUnitAmenitiesAsync.inProgress || store.properties.getPropertyUtilityProvidersV3Async.inProgress || store.properties.updatePropertySetupProgressAsync.inProgress
  })), !editMode && registerActionBack === undefined && registerActionNext === undefined && /*#__PURE__*/React.createElement("div", {
    className: classes.pageFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: handleStep(store.company.companyFeatureStore.isAppfolio ? APPFOLIO_PROPERTY_STEPS.UtilityConfiguration : localStorage.getItem('livable_demo_mode') !== 'Demo' ? PROPERTY_STEPS.UtilityConfiguration : DEMO_PROPERTY_STEPS.UtilityConfiguration),
    variant: "text",
    'custom-attribute': '139-property-unit-setup-page'
  }, t('general.back'), !store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(React.Fragment, null, ': ', t('add_new_property_flow.utilities_setup'))), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: handleSubmit,
    variant: "priority",
    disabled: isSubmited && isSubmitDisabled() || store.units.getUnitsAsync.inProgress || store.units.updateUnitPmsMappingsAsync.inProgress || store.units.updateUnitsAsync.inProgress || store.buildings.getBuildingAsync.inProgress || store.inUnitAmenities.getAllInUnitAmenitiesAsync.inProgress || store.properties.getPropertyUtilityProvidersV3Async.inProgress || store.properties.updatePropertySetupProgressAsync.inProgress,
    'custom-attribute': '140-property-unit-setup-page'
  }, store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(React.Fragment, null, t('buttons.confirm'), ". ", t('general.next')), !store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(React.Fragment, null, t('general.next'), ": ", t('add_new_property_flow.confirm_property')))), editMode && /*#__PURE__*/React.createElement("div", {
    className: classes.xlSection
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: handleSubmit,
    variant: "priority",
    disabled: isSubmited && isSubmitDisabled() || store.units.getUnitsAsync.inProgress || store.units.updateUnitPmsMappingsAsync.inProgress || store.units.updateUnitsAsync.inProgress || store.buildings.getBuildingAsync.inProgress || store.inUnitAmenities.getAllInUnitAmenitiesAsync.inProgress || store.properties.getPropertyUtilityProvidersV3Async.inProgress || store.properties.updatePropertySetupProgressAsync.inProgress,
    'custom-attribute': '141-property-unit-setup-page'
  }, t('buttons.save'))));
});
PropertyUnitSetupPage.propTypes = {
  handleStep: PropTypes.func
};